/**
 * React-query hooks for the Knowledge Base client, wired to the real
 * `/api/plugins/kb/*` endpoints (D12-50 routes + D12-51 `/query`) through the
 * Phase 7 typed API (`../api`) — shapes derive from the server's route defs.
 * Reads use `useQuery`; writes use `useMutation` and invalidate the relevant
 * query keys + `toast` the outcome. The document list polls while any document
 * is still ingesting so the status chips update live without a manual refresh.
 *
 * Query keys: `["kb","workspaces"]` (list) and `["kb","documents",workspaceId]`
 * (a workspace's document page).
 */
import { apiUpload } from "@d12labs/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { kbApi } from "../api";
import { type DocType, type DocumentPage, isInFlight, type UploadResult } from "../types";

/** While a document is ingesting, poll its workspace's list this often (ms). */
const LIVE_POLL_MS = 3000;

const workspacesKey = ["kb", "workspaces"] as const;
const documentsKey = (workspaceId: string) => ["kb", "documents", workspaceId] as const;
const statsKey = (workspaceId: string) => ["kb", "stats", workspaceId] as const;

// ── Workspaces ──────────────────────────────────────────────────────────────

/** List the signed-in user's workspaces. */
export function useWorkspaces() {
	return useQuery({
		queryKey: workspacesKey,
		queryFn: () => kbApi.listWorkspaces(),
	});
}

/** Create a workspace (name only — the server derives a unique slug). */
export function useCreateWorkspace() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (name: string) => {
			const workspace = await kbApi.createWorkspace({ input: { name } });
			// The insert always returns the created row — the `| undefined` in the
			// derived type is noUncheckedIndexedAccess on the repo's `rows[0]`.
			if (!workspace) throw new Error("Request failed");
			return workspace;
		},
		onSuccess: (workspace) => {
			queryClient.invalidateQueries({ queryKey: workspacesKey });
			toast.success(`Workspace "${workspace.name}" created`);
		},
	});
}

/** Delete a workspace (FK cascade wipes its documents/chunks/ingestions). */
export function useDeleteWorkspace() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => kbApi.removeWorkspace({ params: { id } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: workspacesKey });
			toast.success("Workspace deleted");
		},
	});
}

// ── Documents ───────────────────────────────────────────────────────────────

/**
 * The first page of a workspace's documents. Polls every {@link LIVE_POLL_MS}
 * while ANY document is still `pending`/`processing` (live status chips); the
 * interval stops once every document is `ready`/`failed`. Disabled until a
 * workspaceId is known.
 */
export function useDocuments(workspaceId: string | undefined) {
	return useQuery({
		queryKey: documentsKey(workspaceId ?? ""),
		// Pagination is parsed in-handler (no query schema) — the params ride
		// typedRoute's free-form `query` field.
		queryFn: () =>
			kbApi.listDocuments({
				params: { id: workspaceId ?? "" },
				query: { page: 1, pageSize: 100 },
			}),
		enabled: !!workspaceId,
		// react-query passes the latest query result; keep polling only while a
		// document is mid-ingestion, otherwise return false to stop the interval.
		refetchInterval: (query) => {
			const data = query.state.data as DocumentPage | undefined;
			if (!data) return false;
			return data.items.some((d) => isInFlight(d.status)) ? LIVE_POLL_MS : false;
		},
	});
}

/**
 * Aggregate stats for a workspace (KPI tiles). Polls on the same cadence as the
 * document list while `live` (something is ingesting) so the chunk/ready counts
 * climb in step with the status chips; idle otherwise. Disabled until a
 * workspaceId is known.
 */
export function useWorkspaceStats(workspaceId: string | undefined, opts?: { live?: boolean }) {
	return useQuery({
		queryKey: statsKey(workspaceId ?? ""),
		queryFn: () => kbApi.workspaceStats({ params: { id: workspaceId ?? "" } }),
		enabled: !!workspaceId,
		refetchInterval: opts?.live ? LIVE_POLL_MS : false,
	});
}

/**
 * A document reconstructed from its stored chunks (+ a link to the original),
 * for the preview slide-over. Content is static per document, so cache it; the
 * presigned `originalUrl` is valid ~15 min, comfortably longer than the staleTime.
 * Disabled until a documentId is selected.
 */
export function useDocumentPreview(documentId: string | undefined) {
	return useQuery({
		queryKey: ["kb", "preview", documentId ?? ""] as const,
		queryFn: () => kbApi.documentPreview({ params: { id: documentId ?? "" } }),
		enabled: !!documentId,
		staleTime: 60_000,
	});
}

/**
 * Upload one document (multipart) through `apiUpload` — not the typed client,
 * which is JSON-only; `apiUpload` leaves Content-Type unset so the browser writes
 * the multipart boundary itself. The route answers with raw Responses (202
 * enqueue / 200 dedup). Surfaces
 * the 200 `deduped` case with an info toast (identical bytes already in the
 * workspace → no re-ingest).
 */
export function useUploadDocument(workspaceId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (file: File) => {
			const formData = new FormData();
			formData.append("file", file);
			return apiUpload<UploadResult>(
				`/api/plugins/kb/workspaces/${workspaceId}/documents`,
				formData,
			);
		},
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: documentsKey(workspaceId) });
			queryClient.invalidateQueries({ queryKey: statsKey(workspaceId) });
			if (result.deduped) {
				toast.info(`"${result.document.filename}" is already in this workspace`);
			} else {
				toast.success(`"${result.document.filename}" uploaded — indexing…`);
			}
		},
	});
}

/** Delete a document (cascades its chunks/ingestions; removes the blob). */
export function useDeleteDocument(workspaceId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => kbApi.removeDocument({ params: { id } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: documentsKey(workspaceId) });
			queryClient.invalidateQueries({ queryKey: statsKey(workspaceId) });
			toast.success("Document deleted");
		},
	});
}

// ── Query (ask) ─────────────────────────────────────────────────────────────

/**
 * Ask a grounded question against a workspace. The server embeds the question,
 * retrieves the top-K chunks, and synthesizes an answer with cited sources.
 * AI failures surface their exact server body, which the typed caller throws as
 * the error message: a 402 (AI budget exceeded) carries the budget error's own
 * message (`AI daily cap exceeded: $X / $Y…`), any other AI failure is a 502 with
 * the upstream message.
 */
export function useAskQuery(workspaceId: string) {
	return useMutation({
		mutationFn: (input: { question: string; docType?: DocType }) =>
			kbApi.query({
				input: { workspaceId, question: input.question, docType: input.docType },
			}),
	});
}
